import React, { useEffect, useMemo, useRef, useState, useReducer, use } from 'react';
import '../Survey/SurveyPage.css';
import facebookIcon from '../icons/facebook-content.svg';
import instagramIcon from '../icons/instagram-content.svg';
import linkedInIcon from '../icons/linkedin-content.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { contentPlanActions, postActions, questionsActions } from '../../store'; // kept exactly
import KonvaImageEditorModal from '../../components/modals/KonvaEditorModal';
import {
	Layout,
	Row,
	Col,
	Card,
	Input,
	Typography,
	Divider,
	Tag,
	Switch,
	message,
	notification,
	Button,
	Space,
	Select,
	Popconfirm,
	Popover,
	Grid,
	Tooltip, // keep your existing Button import too
} from 'antd';
import {
	SendOutlined,
	ThunderboltOutlined,
	UploadOutlined,
	CloseOutlined,
	CalendarOutlined,
	InstagramOutlined,
	LinkedinOutlined,
	FacebookOutlined,
	PictureOutlined,
	VideoCameraOutlined,
	CopyOutlined,
	EditOutlined,
	CheckOutlined,
	InfoCircleFilled,
	QuestionCircleOutlined,
	DeleteOutlined,
	CameraOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { fetchWrapper } from '../../helpers';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { daysOfWeek } from '../../constants/daysOfWeek';
import PostPhotoEditor from '../../components/Editors/PostPhotoEditor';
import PostPhotoCuter from '../../components/Editors/PostPhotoCuter';
import PostRegenerateEditor from '../../components/Editors/PostContentEditor/PostRegenerateEditor';
import LivePreviewCard from '../../components/Cards/LivePreviewCard';
import { StickyNavigationHeader } from '../../components/Navigation/StickyNavigationHeader';
import ReorderImagesModal from '../../components/modals/ReorderImagesModal/ReorderImagesModal';
import ResponsiveDateTimePicker from '../../components/DateTimePicker/ResponsiveDateTimePicker';
import { useConfirm } from '../../hooks/useConfirm';
import ImageEditorModal from '../../components/modals/ImageEditorModal';
import CameraModal from '../../components/modals/CameraModal';
import threadsIcon from '../icons/threads-mini.svg';
import threadsContentIcon from '../icons/threads-content.svg';
import { forceImageEditReducer, initialForceEdit } from '../../reducers/forceImageEditReducer';
import {
	getImageSizeFromBlob,
	getAllowedAspectRange,
	getAspect,
	isAspectInRange,
	aspectToRatioString,
} from '../../utils/ImageUtils/imageRatio';
import CollaboratorsPicker from '../../components/Forms/CollaboratorsForm';
// ---------------------------------
// Constants & helpers
// ---------------------------------
const baseUrl = `${process.env.REACT_APP_API_URL}`;
const PER_IMAGE_LIMIT = 8 * 1024 * 1024; // 8 MB per image
const IMAGES_TOTAL_LIMIT = 80 * 1024 * 1024; // 80 MB total (images only)
const COMBINED_WARN_LIMIT = 200 * 1000 * 1000; // 64 MB warning (kept for parity)
const MAX_GENERATE_IMAGE_TRIES = 3; // unchanged
const MAX_IMAGES = 10;

const { useBreakpoint } = Grid;

const useNotify = () => {
	const [api, context] = notification.useNotification();
	return { api, context };
};

const useSnTokens = () => {
	const [tokens, setTokens] = useState({
		facebook: false,
		instagram: false,
		linkedin: false,
		threads: false,
	});
	useEffect(() => {
		fetchWrapper
			.get(`${baseUrl}/auth/getSocialMediaTokens/`)
			.then((res) => {
				const next = { facebook: false, instagram: false, linkedin: false, threads: false };
				res.forEach((t) => {
					if (t.socialMediaType === 'FACEBOOK') next.facebook = true;
					if (t.socialMediaType === 'INSTAGRAM') next.instagram = true;
					if (t.socialMediaType === 'LINKEDIN') next.linkedin = true;
					if (t.socialMediaType === 'THREADS') next.threads = true;
				});
				setTokens(next);
			})
			.catch(() => {});
	}, []);
	return tokens;
};

// ---------------------------------
// Component
// ---------------------------------
export const PostPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { contentId } = useParams(); // kept though unused

	// Store slices (unchanged)
	const user = useSelector((s) => s.auth.user);
	const post = useSelector((s) => s.post.post);
	const { loading: postLoading, generating: postGenerating } = useSelector((s) => s.post);
	const contentPlan = useSelector((s) => s.contentPlan.contentPlan);

	// Confirm whether this post is their own, or it is on moderation
	const isMyPost = post.UserId === user.id;

	// Local state
	const videoPlayerRef = useRef(null);
	const hiddenVideoInput = useRef(null);
	const hiddenFileInput = useRef(null);
	const hiddenReferenceInput = useRef(null);

	const [images, setImages] = useState([]);
	const [video, setVideo] = useState(null);
	const [generating, setGenerating] = useState(false);
	const totalImagesCount = (post?.images?.length || 0) + images.length;

	const [editMainText, setEditMainText] = useState(false);
	const [editHashtag, setEditHashtag] = useState(false);
	const [editOffer, setEditOffer] = useState(false);
	const [dragFrom, setDragFrom] = useState(null);
	const [dragOver, setDragOver] = useState(null);
	const [reorderMode, setReorderMode] = useState(false);
	const [pendingOrder, setPendingOrder] = useState([]);
	const [showReorderModal, setShowReorderModal] = useState(false);
	const [showCameraModal, setShowCameraModal] = useState(false);
	const serverImagesCount = post?.images?.length || 0;

	// Проверка мобильного устройства
	const isMobileDevice = () => {
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
			navigator.userAgent,
		);
	};

	const [mainText, setMainText] = useState(post?.post ?? '');
	const [hashtags, setHashtags] = useState(post?.hashtags ?? '');
	const [offer, setOffer] = useState(post?.media ?? '');

	const [publishTime, setPublishTime] = useState(contentPlan.date);
	const [weekDay, setWeekDay] = useState(contentPlan.weekDay || '');

	const [dragActive, setDragActive] = useState(false);
	const snTokens = useSnTokens();
	const [snForAnotherPost, setSnForAnotherPost] = useState([]);

	const isReadOnly = Boolean(post?.published);

	const [editingImage, setEditingImage] = useState(null);
	const [showImageEditor, setShowImageEditor] = useState(false);
	const [referenceImages, setReferenceImages] = useState([]);
	const MAX_REFERENCE_IMAGES = 3;
	const previewVideoUrl = post?.video ? post.video : video ? URL.createObjectURL(video) : null;

	const [editCollabs, setEditCollabs] = useState(false);
	const [collaborators, setCollaborators] = useState(post?.collaborators?.collaborators || []);

	const { api: notify, context: notificationContextHolder } = useNotify();

	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const [forceEdit, dispatchForceEdit] = useReducer(forceImageEditReducer, initialForceEdit);

	const [activeEdit, setActiveEdit] = useState(null);

	// ---------------------------------
	// Effects
	// ---------------------------------

	useEffect(() => {
		const initial = [];
		if (contentPlan.socialNetwork?.includes('LinkedIn')) initial.push('LinkedIn');
		if (contentPlan.socialNetwork?.includes('Facebook')) initial.push('Facebook');
		if (contentPlan.socialNetwork?.includes('Instagram')) initial.push('Instagram');
		if (contentPlan.socialNetwork?.includes('Threads')) initial.push('Threads');
		setSnForAnotherPost(initial);
	}, []);

	useEffect(() => {
		if (contentPlan.socialNetwork?.toLowerCase().includes('facebook')) {
			fetchWrapper.get(`${baseUrl}/profile/`).then((res) => {
				if (!res.facebookPageId) openNotificationFbPages();
			});
		}
	}, []);

	useEffect(() => {
		const total = [...images, video].reduce((acc, m) => acc + (m?.size || 0), 0);
		if (total > COMBINED_WARN_LIMIT) message.error(t('post.bigMediaListSize', { size: 200 }));
	}, [images, video]);

	useEffect(() => {
		if (reorderMode) {
			const n = post?.images?.length || 0;
			setPendingOrder([...Array(n).keys()]);
		}
	}, [reorderMode, post?.images?.length]);

	useEffect(() => {
		setCollaborators(post?.collaborators?.collaborators || []);
	}, [post?.collaborators]);

	useEffect(() => {
		if (!forceEdit.visible && forceEdit.edited.length) {
			(async () => {
				await autoUploadImages(forceEdit.edited);
				dispatchForceEdit({ type: 'CANCEL' });
			})();
		}
	}, [forceEdit.visible]);

	const handleReferenceImageSelect = (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;

		if (referenceImages.length + files.length > MAX_REFERENCE_IMAGES) {
			message.error(
				t(
					'postPage.errors.tooManyReferences',
					`Максимум ${MAX_REFERENCE_IMAGES} референсных изображений`,
				),
			);
			e.target.value = '';
			return;
		}

		const validFiles = [];
		for (const file of files) {
			if (file.size > PER_IMAGE_LIMIT) {
				message.error(t('functions.perImageTooLarge'));
				continue;
			}
			if (!file.type.startsWith('image/')) {
				message.error(t('postPage.errors.onlyImagesAllowed', 'Допускаются только изображения'));
				continue;
			}

			const img = new Image();
			img.onload = () => {
				const canvas = document.createElement('canvas');
				const size = Math.min(img.width, img.height);
				canvas.width = size;
				canvas.height = size;

				const ctx = canvas.getContext('2d');
				const offsetX = (img.width - size) / 2;
				const offsetY = (img.height - size) / 2;

				ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);

				canvas.toBlob((blob) => {
					if (blob) {
						const squareFile = new File([blob], file.name, { type: file.type });
						validFiles.push(squareFile);

						if (validFiles.length === files.length) {
							setReferenceImages((prev) => [...prev, ...validFiles]);
							message.success(
								t(
									'postPage.message.referencesAdded',
									`Добавлено ${validFiles.length} референс(ов)`,
								),
							);
						}
					}
				}, file.type);
			};

			img.src = URL.createObjectURL(file);
		}

		e.target.value = '';
	};

	// ---------------------------------
	// Notifications
	// ---------------------------------
	const openNotificationSocialNetwork = ({ message: msg, description }) => {
		const key = `open${Date.now()}`;
		const btn = (
			<Space>
				<Button type="primary" size="small" onClick={() => navigate('/profile')}>
					{t('posts.goToProfile')}
				</Button>
			</Space>
		);
		notify.open({
			message: msg || t('functions.error'),
			description: description || t('posts.unknownError'),
			btn,
			key,
		});
	};

	const openNotificationFbPages = () => {
		const key = `open${Date.now()}`;
		const btn = (
			<Space>
				<Button type="primary" size="small" onClick={() => navigate('/profile?helpFbModal=true')}>
					{t('posts.goToProfile')}
				</Button>
			</Space>
		);
		notify.open({
			message: t('functions.warning'),
			description: t('posts.notSelectedFacebookPage'),
			btn,
			key,
		});
	};

	// ---------------------------------
	// Helpers
	// ---------------------------------
	const clipboardCopy = (text) => {
		navigator.clipboard.writeText(text);
		message.success(t('functions.copied'));
	};

	const enforceTextLimits = () => {
		const textToCheck = editMainText ? mainText : post?.post;
		if (!textToCheck) return true;

		const len = textToCheck.length;
		let ok = true;
		let badNet = null;

		contentPlan.socialNetwork.split(',').forEach((n) => {
			const k = n.trim().toLowerCase();
			if (k.includes('instagram') && len > 2200) {
				badNet = 'Instagram';
				ok = false;
			}
			if (k.includes('linkedin') && len > 3000) {
				badNet = 'LinkedIn';
				ok = false;
			}
			if (k.includes('facebook') && len > 8000) {
				badNet = 'Facebook';
				ok = false;
			}
		});

		if (!ok) {
			const key = `char-limit-${Date.now()}`;
			const limit = badNet === 'Instagram' ? 2200 : badNet === 'LinkedIn' ? 3000 : 8000;
			notify.warning({
				message: t('functions.warning'),
				description: (
					<>
						{t('functions.characterLimitError', { network: badNet, limit })}
						<div style={{ marginTop: 10 }}>{t('functions.pleaseEditPost')}</div>
					</>
				),
				btn: (
					<Button type="primary" size="small" onClick={() => notify.destroy(key)}>
						{t('functions.close')}
					</Button>
				),
				key,
				duration: 0,
			});
		}
		return ok;
	};

	const patchPost = async (partial) => {
		try {
			const res = await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, partial);
			return res;
		} catch (e) {
			message.error(t('functions.wrong'));
			throw e;
		}
	};

	const requireInstagramMedia = () => {
		if (
			contentPlan.socialNetwork.toLowerCase().includes('instagram') &&
			!post?.images?.length &&
			!post?.video &&
			!images.length &&
			!video
		) {
			message.warning(t('functions.minMediaForInstagramPostMessage'));
			return false;
		}
		return true;
	};

	const handleSaveEditedImage = async (editedFile) => {
		if (!editingImage) return;

		const { index } = editingImage;

		if (index < images.length) {
			const updated = [...images];
			updated[index] = editedFile;
			setImages(updated);
		} else {
			await dispatch(
				postActions.uploadImage({
					postId: post.id,
					contentPlanId: contentPlan.id,
					images: [editedFile],
					mode: 'replace',
					replaceIndex: index - images.length,
				}),
			);
		}

		setEditingImage(null);
	};

	// ---------------------------------
	// Image upload
	// ---------------------------------
	const autoUploadImages = async (files) => {
		if (!files?.length) return;

		try {
			await dispatch(
				postActions.uploadImage({
					postId: post.id,
					contentPlanId: contentPlan.id,
					images: files,
					mode: 'append',
				}),
			);
			message.success(t('postPage.message.imgSuccess'));
		} catch (e) {
			message.error(t('postPage.message.imgError'));
		}
	};

	const preflightImagesBatch = (prevImages, incomingFiles, serverCount = 0) => {
		const incomingImages = incomingFiles.filter((f) => f.type.includes('image'));

		if (post?.collaborators?.collaborators && post.collaborators.collaborators.length > 0) {
			const currentMediaCount =
				(prevImages.length || 0) + (serverCount || 0) + (video ? 1 : 0) + (post?.video ? 1 : 0);
			if (currentMediaCount + incomingImages.length > 1) {
				message.error('При наличии соавторов можно добавить только одно медиа');
				return { ok: false };
			}
		}

		if (incomingImages.length > MAX_IMAGES) {
			message.error(t('functions.tooManyImages'));
			return { ok: false };
		}

		const currentCount = (prevImages.length || 0) + (serverCount || 0);
		if (currentCount + incomingImages.length > MAX_IMAGES) {
			const left = Math.max(0, MAX_IMAGES - currentCount);
			message.error(
				left ? t('functions.tooManyImagesLeft', { left }) : t('functions.tooManyImages'),
			);
			return { ok: false };
		}

		const oversize = incomingImages.find((f) => f.size > PER_IMAGE_LIMIT);
		if (oversize) {
			message.error(t('functions.perImageTooLarge'));
			return { ok: false };
		}

		const merged = [...prevImages, ...incomingImages];
		const total = merged.reduce((s, f) => s + (f?.size || 0), 0);
		if (total > IMAGES_TOTAL_LIMIT) {
			message.error(t('functions.totalImagesTooLarge'));
			return { ok: false };
		}

		return { ok: true, images: incomingImages };
	};

	const handleForceEditorSave = async (editedFile) => {
		const range = getAllowedAspectRange({
			socialNetwork: contentPlan?.socialNetwork,
			isStories: !!post?.isStories,
		});

		const { width, height } = await getImageSizeFromBlob(editedFile);
		const aspect = getAspect(width, height);

		if (!isAspectInRange(aspect, range)) {
			message.error(
				t('postPage.message.aspectOutRange', {
					aspect: aspectToRatioString(aspect),
					min: range.minLabel || aspectToRatioString(range.min),
					max: range.maxLabel || aspectToRatioString(range.max),
				}),
			);
			return;
		}

		dispatchForceEdit({ type: 'SAVE_CURRENT', file: editedFile });
		message.success(t('imageEditor.messages.saveSuccess'));
	};

	// ---------------------------------
	// Change handlers (server updates preserved)
	// ---------------------------------
	const changeMainText = async () => {
		if (isReadOnly) {
			return message.info(
				t('postPage.message.cannotEditPublished') || "Published post can't be edited",
			);
		}

		if (activeEdit && activeEdit !== 'mainText') {
			await cancelEdit(activeEdit);
		}

		if (editMainText) {
			if (mainText.length > 8000)
				return message.error(t('functions.maxTextLength', { limit: 8000 }));
			if (!mainText.trim()) return message.error(t('functions.noText'));

			if (mainText.trim().length < 25)
				return message.error(t('functions.minTextLength', { limit: 25 }));

			const res = await patchPost({ post: mainText });
			if (res) {
				dispatch(
					postActions.updatePost({
						postId: post.id,
						contentPlanId: contentPlan.id,
						postData: { post: mainText },
					}),
				);
				message.success(t('functions.changesSaved'));
				setActiveEdit(null);
			}
		} else {
			setActiveEdit('mainText');
		}
		setEditMainText((v) => !v);
	};

	const changeHashtags = async () => {
		if (isReadOnly) {
			return message.info(
				t('postPage.message.cannotEditPublished') || "Published post can't be edited",
			);
		}

		if (activeEdit && activeEdit !== 'hashtags') {
			await cancelEdit(activeEdit);
		}

		if (editHashtag) {
			if (!hashtags.trim()) return message.error(t('functions.noText'));
			const res = await patchPost({ hashtags });
			if (res) {
				dispatch(
					postActions.updatePost({
						postId: post.id,
						contentPlanId: contentPlan.id,
						postData: { hashtags },
					}),
				);
				message.success(t('functions.changesSaved'));
				setActiveEdit(null);
			}
		} else {
			setActiveEdit('hashtags');
		}
		setEditHashtag((v) => !v);
	};

	const changeOffer = async () => {
		if (isReadOnly) {
			return message.info(t('post.cannotEditPublished') || "Published post can't be edited");
		}

		if (activeEdit && activeEdit !== 'offer') {
			await cancelEdit(activeEdit);
		}

		if (editOffer) {
			const res = await patchPost({ media: offer });
			if (res) {
				dispatch(
					postActions.updatePost({
						postId: post.id,
						contentPlanId: contentPlan.id,
						postData: { media: offer },
					}),
				);
				message.success(t('functions.changesSaved'));
				setActiveEdit(null);
			}
		} else {
			setActiveEdit('offer');
		}
		setEditOffer((v) => !v);
	};

	const changePublishTime = (iso) => {
		if (isReadOnly) {
			return message.info(
				t('postPage.message.cannotEditPublished') || 'Нельзя менять дату у опубликованного поста',
			);
		}
		setPublishTime(iso);
		fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, { date: iso });
	};

	const changeWeekDay = (value) => {
		if (isReadOnly) {
			return message.info(
				t('postPage.message.cannotEditPublished') || 'Нельзя менять дату у опубликованного поста',
			);
		}
		fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, { weekday: value }).then((res) => {
			if (res) setWeekDay(value);
		});
	};

	const saveCollaborators = async () => {
		if (isReadOnly) {
			return message.info(
				t('postPage.message.cannotEditPublished') || "Published post can't be edited",
			);
		}

		try {
			dispatch(
				postActions.updatePost({
					postId: post.id,
					contentPlanId: contentPlan.id,
					postData: { collaborators: collaborators },
				}),
			);
			message.success(t('functions.changesSaved'));
			setEditCollabs(false);
			setActiveEdit(null);
		} catch (e) {
			message.error(t('functions.wrong'));
			throw e;
		}
	};

	const cancelEdit = async (type) => {
		switch (type) {
			case 'mainText':
				setMainText(post?.post || '');
				setEditMainText(false);
				break;
			case 'hashtags':
				setHashtags(post?.hashtags || '');
				setEditHashtag(false);
				break;
			case 'offer':
				setOffer(post?.media || '');
				setEditOffer(false);
				break;
			case 'collabs':
				setCollaborators(post?.collaborators?.collaborators || []);
				setEditCollabs(false);
				break;
		}
		setActiveEdit(null);
		message.info(t('postPage.message.editCancelled') || 'Редактирование отменено');
	};

	// ---------------------------------
	// Media: images
	// ---------------------------------

	const handleImageFilePicker = async (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;

		if (post?.isStories) {
			const totalMedia =
				(post?.images?.length || 0) + images.length + (video ? 1 : 0) + (post?.video ? 1 : 0);
			if (totalMedia >= 1 || files.length > 1) {
				message.error(t('postPage.message.storiesLimit'));
				e.target.value = '';
				return;
			}
		}

		const serverCount = post?.images?.length || 0;
		const check = preflightImagesBatch(images, files, serverCount);
		if (!check.ok) {
			e.target.value = '';
			return;
		}

		dispatchForceEdit({ type: 'START', files: check.images });
		e.target.value = '';
	};

	const handleCameraCapture = async (file) => {
		if (!file) return;

		if (post?.isStories) {
			const totalMedia =
				(post?.images?.length || 0) + images.length + (video ? 1 : 0) + (post?.video ? 1 : 0);
			if (totalMedia >= 1) {
				message.error(t('postPage.message.storiesLimit'));
				return;
			}
		}

		const serverCount = post?.images?.length || 0;
		const check = preflightImagesBatch(images, [file], serverCount);
		if (!check.ok) {
			return;
		}

		dispatchForceEdit({ type: 'START', files: check.images });
	};

	const confirm = useConfirm();

	const handleDeleteImage = async (idx) => {
		const isUserAgree = await confirm({
			title: t('post.removeSingleMediaTitle') || 'Подтверждение',
			message:
				t('post.removeSingleMediaConfirm') || 'Вы уверены, что хотите удалить это изображение?',
			confirmText: t('common.yes') || 'Да',
			cancelText: t('common.no') || 'Нет',
		});

		if (!isUserAgree) return;

		setImages((prev) => prev.filter((_, i) => i !== idx));
	};

	const handleRemoveImage = async (removeIndex) => {
		const isUserAgree = await confirm({
			title: t('post.removeSingleMediaConfirm') || 'Удалить это изображение?',
			confirmText: t('common.yes') || 'Да',
			cancelText: t('common.no') || 'Нет',
		});
		if (!isUserAgree) return;
		try {
			await dispatch(
				postActions.removeImage({
					postId: post.id,
					contentPlanId: contentPlan.id,
					removeIndex,
				}),
			).unwrap();

			message.success(t('postPage.message.imageRemoved') || 'Изображение удалено');
		} catch (e) {
			message.error(t('functions.wrong') || 'Что-то пошло не так');
		}
	};

	const handleResetImages = async () => {
		const isUserAgree = await confirm({
			title: t('post.removeAllMediaTitle') || 'Подтверждение',
			message: t('post.removeAllMediaConfirm') || 'Вы уверены, что хотите удалить все изображения?',
			confirmText: t('common.yes') || 'Да',
			cancelText: t('common.no') || 'Нет',
		});

		if (!isUserAgree) return;

		setImages([]);
	};

	const renderEditButton = (type, currentEdit, onClick, disabled = false) => {
		const isActive = activeEdit === type;
		const isEditing = currentEdit;

		return (
			<Button
				icon={isEditing ? <CheckOutlined /> : <EditOutlined />}
				onClick={onClick}
				disabled={disabled || (activeEdit && activeEdit !== type)}
				type={isActive ? 'primary' : 'default'}
			>
				{isEditing ? t('postPage.buttons.save') : t('postPage.buttons.edit')}
			</Button>
		);
	};

	const handleRemoveGeneratedImage = async (index = null) => {
		const isUserAgree = await confirm({
			title: t('post.removeAllMediaConfirm') || 'Подтверждение удаления',
			confirmText: t('common.yes') || 'Да',
			cancelText: t('common.no') || 'Нет',
		});

		if (!isUserAgree) return;

		if (index === null) {
			await dispatch(
				postActions.removeAllImages({
					removeIndex: 0,
					postId: post.id,
					contentPlanId: contentPlan.id,
				}),
			);
		} else {
			await fetchWrapper.put(`${baseUrl}/post/update/${post.id}`, {
				images: null,
				hashtags,
				media: offer,
			});
			await dispatch(
				postActions.removeAllImages({
					removeIndex: 0,
					postId: post.id,
					contentPlanId: contentPlan.id,
				}),
			);
		}
	};

	const handleSaveModalOrder = async (orderArr) => {
		try {
			await fetchWrapper.put(`${baseUrl}/post/reorder-images/${post.id}`, { order: orderArr });
			await dispatch(postActions.getPost(contentId)).unwrap();
			message.success(t('postPage.message.imgReorderSuccess'));
			setShowReorderModal(false);
		} catch (err) {
			message.error(t('functions.wrongOrderImage'));
		}
	};

	// ---------------------------------
	// Media: video (logic preserved – disabled upload path)
	// ---------------------------------
	const handleAddVideo = (e) => {
		if (post?.isStories) {
			const totalMedia =
				(post?.images?.length || 0) + images.length + (video ? 1 : 0) + (post?.video ? 1 : 0);
			if (totalMedia >= 1) {
				message.error(t('postPage.message.storiesLimit'));
				e.target.value = '';
				return;
			}
		}

		const f = e.target.files?.[0];
		if (!f) return;
		if ((f.size / 1000000).toFixed() > 200) return message.error(t('post.maxVideoSize'));
		setVideo(f);
	};

	const handleManageMetaData = () => {
		if (videoPlayerRef.current) {
			if (videoPlayerRef.current.duration > 595) {
				message.error(t('post.maxVideoDuration'));
				setVideo(null);
			}
		}
	};

	const handleRemoveVideo = async () => {
		setVideo(null);
		if (post.video) {
			try {
				await dispatch(
					postActions.removeVideo({ postId: post.id, contentPlanId: contentPlan.id, video }),
				);
				window.location.reload();
			} catch (err) {
				message.error(t('functions.wrong'));
			}
		}
	};

	// Drag & drop
	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!!activeEdit) return;
		setDragActive(true);
	};
	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!!activeEdit) return;
		setDragActive(false);
	};
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!!activeEdit) return;
	};
	const handleDrop = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!!activeEdit) return;
		setDragActive(false);

		const list = Array.from(e.dataTransfer.files || []);
		if (!list.length) return;

		const droppedImages = list.filter((f) => f.type.includes('image'));
		const droppedVideos = list.filter((f) => f.type.includes('video'));

		if (post?.isStories) {
			const totalMedia =
				(post?.images?.length || 0) + images.length + (video ? 1 : 0) + (post?.video ? 1 : 0);
			const droppedCount = (droppedImages.length || 0) + (droppedVideos.length || 0);
			if (totalMedia >= 1 || droppedCount > 1) {
				message.error(t('postPage.message.storiesLimit'));
				return;
			}
		}

		if (droppedImages.length) {
			const serverCount = post?.images?.length || 0;
			const check = preflightImagesBatch(images, droppedImages, serverCount);
			if (!check.ok) {
				const v = droppedVideos[0];
				if (v) {
					const mb = v.size / 1_000_000;
					if (mb > 200) message.error(t('post.maxVideoSize'));
					else setVideo(v);
				}
				return;
			}

			dispatchForceEdit({ type: 'START', files: check.images });
		}

		const v = droppedVideos[0];
		if (v) {
			const mb = v.size / 1_000_000;
			if (mb > 200) message.error(t('post.maxVideoSize'));
			else setVideo(v);
		}
	};

	const onImageDragStart = (index) => (e) => {
		if (!reorderMode) return;

		setDragFrom(index);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/plain', String(index));

		const imgEl = e.currentTarget.querySelector('img');
		if (imgEl) {
			const rect = imgEl.getBoundingClientRect();
			const offsetX = e.clientX - rect.left;
			const offsetY = e.clientY - rect.top;

			e.dataTransfer.setDragImage(imgEl, offsetX, offsetY);
		}
	};
	const onImageDragEnter = (index) => (e) => {
		e.preventDefault();
		if (index !== dragOver) setDragOver(index);
	};
	const onImageDragOver = (e) => {
		e.preventDefault();
	};
	const onImageDrop = (newIndex) => async (e) => {
		e.preventDefault();
		if (!reorderMode) return;

		if (dragFrom === null || newIndex === dragFrom) {
			setDragFrom(null);
			setDragOver(null);
			return;
		}

		setPendingOrder((prev) => {
			const next = [...prev];
			const fromIdx = dragFrom;
			const toIdx = newIndex;

			[next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
			return next;
		});

		setDragFrom(null);
		setDragOver(null);
	};

	const onImageDragEnd = () => {
		setDragFrom(null);
		setDragOver(null);
	};

	// ---------------------------------
	// Save / Upload flow (logic preserved)
	// ---------------------------------
	const persistTextIfEditing = async () => {
		if (isReadOnly) return true;
		if (!editMainText) return true;

		const res = await patchPost({ post: mainText });
		if (res) {
			dispatch(
				postActions.updatePost({
					postId: post.id,
					contentPlanId: contentPlan.id,
					postData: { post: mainText },
				}),
			);
			setEditMainText(false);
			return true;
		}

		return false;
	};

	const handleRemoveReference = (index) => {
		setReferenceImages((prev) => prev.filter((_, i) => i !== index));
	};

	const handleClearReferences = () => {
		setReferenceImages([]);
	};

	const onGenerateImage = async () => {
		if (post.countGenerateImage >= MAX_GENERATE_IMAGE_TRIES) {
			return message.error('Превышен лимит генераций изображений на пост: 3 попытки.');
		}

		const isUserAgree = await confirm({
			title: t('post.postGenerateImageLimit') || 'Лимит генерации',
			message: `${t('post.postGenerateImageLeft')} ${
				MAX_GENERATE_IMAGE_TRIES - +post.countGenerateImage
			}\n${t('functions.continue')}?`,
			confirmText: t('common.yes') || 'Да',
			cancelText: t('common.no') || 'Нет',
		});

		if (!isUserAgree) return;

		setGenerating(true);
		message.loading(t('post.generatingImage'));

		const payload = {
			postId: post.id,
			contentPlanId: contentPlan.id,
			referenceImages: referenceImages,
		};

		dispatch(postActions.generateImage(payload))
			.unwrap()
			.then(() => {
				message.success(t('functions.successfullyGenerated'));
				setReferenceImages([]);
			})
			.catch(() => message.error(t('functions.wrong')))
			.finally(() => {
				setGenerating(false);
				setImages([]);
			});
	};

	const handleToogleSnForAnotherPost = (sn) => {
		setSnForAnotherPost((prev) => {
			if (prev.includes(sn)) return prev.filter((i) => i !== sn);
			if (snTokens[sn.toLowerCase()]) return [...prev, sn];
			openNotificationSocialNetwork({
				message: t('functions.error'),
				description: t(`posts.notLinked-${sn.toLowerCase()}`),
			});
			return prev;
		});
	};

	const handleGenerateAnotherPost = () => {
		const newPostDescription = `${t('post.textToGenerateAnotherPost')}: ${post.post}`;
		const date = new Date();
		fetchWrapper
			.post(`${baseUrl}/post/save`, {
				description: newPostDescription,
				socialNetwork: snForAnotherPost.toString(),
				date: date.toISOString(),
				formatId: null,
			})
			.then((res) => {
				if (res) {
					navigate(`/create-post/${res.contentPlanId}`);
					window.location.reload();
				}
			})
			.catch(() => message.error(t('functions.wrong')));
	};

	const onOkayButton = async () => {
		if (isReadOnly) {
			return message.info(t('post.cannotEditPublished') || "Published post can't be edited");
		}

		if (forceEdit.visible) {
			return message.warning(t('postPage.message.finCrop'));
		}

		if (!enforceTextLimits()) return;
		if (!requireInstagramMedia()) return;

		if (!(await persistTextIfEditing())) return;

		if (video && !post?.images?.length) {
			try {
				await dispatch(
					postActions.uploadVideo({ postId: post.id, contentPlanId: contentPlan.id, video }),
				);
			} catch {
				message.error(t('functions.wrong'));
			}
		}

		if (images.length) {
			try {
				await dispatch(
					postActions.uploadImage({ postId: post.id, contentPlanId: contentPlan.id, images }),
				);
				if (contentPlan.forIndependentPosts) {
					navigate('/posts');
					window.location.reload();
				} else {
					navigate('/content-plan');
					window.location.reload();
				}
			} catch {
				message.error(t('functions.wrong'));
			}
			return;
		}

		if (post?.UserId && post?.UserId !== user?.id) return navigate('/moderation');
		if (contentPlan?.forIndependentPosts) {
			navigate('/posts');
			window.location.reload();
		} else {
			navigate('/content-plan');
			window.location.reload();
		}
	};

	const range = getAllowedAspectRange({
		socialNetwork: contentPlan?.socialNetwork,
		isStories: !!post?.isStories,
	});

	// ---------------------------------
	// JSX
	// ---------------------------------
	return (
		<Layout style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #ffffff, #faf5ff)' }}>
			<StickyNavigationHeader
				headerTitle={t('post.titlePage')}
				onBackClick={() => {
					navigate(-1);
				}}
				actions={
					<Space>
						<Button icon={<ThunderboltOutlined />} type="default" disabled>
							{t('post.improveBut')}
						</Button>
						<Button
							icon={<SendOutlined />}
							type="primary"
							onClick={onOkayButton}
							disabled={activeEdit !== null}
						>
							{t('post.saveBut')}
						</Button>
					</Space>
				}
			/>

			<Layout.Content
				style={{ maxWidth: 1200, margin: '24px auto', width: '100%', padding: '0 16px' }}
			>
				{notificationContextHolder}

				<Row gutter={[16, 16]}>
					<Col xs={24} lg={14}>
						<Card bordered className="shadow-sm" bodyStyle={{ padding: 16 }}>
							<Row align="middle" justify="space-between">
								<Space size={12} align="center">
									<Typography.Title level={5} style={{ margin: 0 }}>
										{t('postPage.cards.targetScheduleTitle')}
									</Typography.Title>
								</Space>
								<Space split={<Divider type="vertical" />} style={{ color: '#8c8c8c' }}>
									{contentPlan?.socialNetwork?.includes('Instagram') && (
										<Space>
											<InstagramOutlined />
											Instagram
										</Space>
									)}
									{contentPlan?.socialNetwork?.includes('LinkedIn') && (
										<Space>
											<LinkedinOutlined />
											LinkedIn
										</Space>
									)}
									{contentPlan?.socialNetwork?.includes('Facebook') && (
										<Space>
											<FacebookOutlined />
											Facebook
										</Space>
									)}
									{contentPlan?.socialNetwork?.includes('Threads') && (
										<Space>
											<img src={threadsIcon} alt="Threads" style={{ width: 14, height: 14 }} />
											Threads
										</Space>
									)}
									<Space>
										<CalendarOutlined />
										{dayjs(publishTime).format('MMM D, HH:mm')}
									</Space>
								</Space>
								{post?.isStories && (
									<Space style={{ color: '#1677ff' }}>
										<VideoCameraOutlined />
										Stories
									</Space>
								)}
							</Row>

							<Divider style={{ margin: '12px 0' }} />

							<Row gutter={[12, 12]}>
								<Col xs={24} md={12}>
									<Card size="small" bordered>
										<Space direction="vertical" style={{ width: '100%' }}>
											<Typography.Text strong>{t('postPage.cards.when')}</Typography.Text>
											{contentPlan?.weekDay ? (
												<div className="flex items-center gap-2">
													<Select
														onChange={changeWeekDay}
														value={weekDay}
														className="w-full"
														options={daysOfWeek.map((d) => ({ value: d, label: d }))}
													/>
													<input
														readOnly={post?.published}
														className="border rounded px-2 py-1 w-[110px]"
														type="time"
														value={moment(publishTime).format('HH:mm')}
														onChange={(e) => {
															const value = e.target.value;
															if (value) {
																const newDate = new Date(publishTime).setHours(
																	value.split(':')[0],
																	value.split(':')[1],
																	0,
																);
																changePublishTime(new Date(newDate).toISOString());
															}
														}}
													/>
												</div>
											) : (
												<ResponsiveDateTimePicker
													value={dayjs(publishTime)}
													onChange={(d) => d && changePublishTime(d.toISOString())}
													placeholder={t('postPage.cards.when')}
													minDate={new Date()}
													disabledDate={(current) => current && current < dayjs().startOf('day')}
													disabled={isReadOnly}
												/>
											)}
										</Space>
									</Card>
								</Col>
								<Col xs={24} md={12}>
									<Card size="small" bordered className="opacity-50 cursor-not-allowed">
										<Space direction="vertical" style={{ width: '100%' }}>
											<Typography.Text strong>
												{t('postPage.cards.autoOptimizationTitle')}
											</Typography.Text>
											<Row justify="space-between" align="middle">
												<Typography.Text>
													{t('postPage.cards.bestTimeRecommendations')}
												</Typography.Text>
												<Switch defaultChecked />
											</Row>
											<Row justify="space-between" align="middle">
												<Typography.Text>
													{t('postPage.cards.autoRescheduleOnConflict')}
												</Typography.Text>
												<Switch />
											</Row>
										</Space>
									</Card>
								</Col>
							</Row>
						</Card>

						<Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
							<Row justify="space-between" align="middle">
								<Typography.Title level={5} style={{ margin: 0 }}>
									{t('postPage.cards.postContentTitle')}
								</Typography.Title>
								<Space>
									<Button icon={<CopyOutlined />} onClick={() => clipboardCopy(post?.post || '')}>
										{t('postPage.buttons.copy')}
									</Button>
									{renderEditButton('mainText', editMainText, changeMainText, isReadOnly)}
								</Space>
							</Row>

							<Divider style={{ margin: '12px 0' }} />

							{editMainText ? (
								<div className="relative">
									<Input.TextArea
										value={mainText}
										onChange={(e) => setMainText(e.target.value)}
										autoSize={{ minRows: 8, maxRows: 16 }}
										maxLength={8000}
										placeholder="Write something engaging…"
									/>
									<div
										className={`absolute -bottom-6 right-2 bg-white rounded px-2 py-1 text-xs ${
											mainText.length > 8000 ? 'text-red-500' : 'text-gray-600'
										}`}
									>
										{mainText.length} / 8000
									</div>
								</div>
							) : (
								<Typography.Paragraph className="!mb-0" style={{ whiteSpace: 'pre-line' }}>
									{post?.post || mainText}
								</Typography.Paragraph>
							)}
						</Card>

						{String(contentPlan.socialNetwork).toLowerCase().includes('instagram') &&
							!post?.isStories && (
								<Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
									<Row align="middle" justify="space-between">
										<Space align="center">
											<Typography.Title level={5} style={{ margin: 0 }}>
												{t('postPage.cards.collaborators')}
											</Typography.Title>
											<Tooltip title={t('postPage.hint.collaborators')} placement="top">
												<QuestionCircleOutlined
													style={{ color: '#8c8c8c', fontSize: 14, cursor: 'help' }}
												/>
											</Tooltip>
										</Space>
										{!isReadOnly && (
											<Space>
												{editCollabs ? (
													<>
														<Button type="primary" onClick={saveCollaborators}>
															{t('postPage.buttons.save')}
														</Button>
														<Button onClick={() => cancelEdit('collabs')}>
															{t('functions.close') || 'Cancel'}
														</Button>
													</>
												) : (
													<Button
														onClick={() => {
															if (activeEdit && activeEdit !== 'collabs') {
																cancelEdit(activeEdit);
															}
															setEditCollabs(true);
															setActiveEdit('collabs');
														}}
														icon={<EditOutlined />}
														disabled={activeEdit && activeEdit !== 'collabs'}
													>
														{t('postPage.buttons.edit')}
													</Button>
												)}
											</Space>
										)}
									</Row>
									<Divider style={{ margin: '12px 0' }} />

									{post?.isStories && (
										<Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
											{t('posts.storiesWithoutCollab') || 'Stories публикуются без соавторов.'}
										</Typography.Text>
									)}

									{editCollabs && !isReadOnly ? (
										<CollaboratorsPicker
											value={collaborators}
											onChange={setCollaborators}
											isStories={post?.isStories}
											disabled={isReadOnly}
											max={5}
											t={t}
										/>
									) : (
										<Space size={[0, 8]} wrap>
											{(post?.collaborators?.collaborators?.length
												? post.collaborators.collaborators
												: collaborators
											).map((c) => (
												<Tag key={c} style={{ margin: '4px' }}>
													@{c}
												</Tag>
											))}
											{(!post?.collaborators?.collaborators ||
												post.collaborators.collaborators.length === 0) &&
												collaborators.length === 0 && (
													<Typography.Text type="secondary" style={{ fontSize: 12 }}>
														{t('postPage.placeholders.collaboratorsNoPlaceholder') ||
															'Соавторы не указаны.'}
													</Typography.Text>
												)}
										</Space>
									)}
								</Card>
							)}

						<Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
							<Row justify="space-between" align="middle">
								<Typography.Title level={5} style={{ margin: 0 }}>
									{t('postPage.cards.hashtagsTitle')}
								</Typography.Title>
								<Space>
									<Button
										icon={<CopyOutlined />}
										onClick={() => clipboardCopy(post?.hashtags || '')}
									>
										{t('postPage.buttons.copy')}
									</Button>
									{renderEditButton('hashtags', editHashtag, changeHashtags, isReadOnly)}
								</Space>
							</Row>
							<Divider style={{ margin: '12px 0' }} />
							{editHashtag ? (
								<Input.TextArea
									value={hashtags}
									onChange={(e) => setHashtags(e.target.value)}
									autoSize={{ minRows: 3, maxRows: 8 }}
									placeholder="#marketing #b2b #casestudy"
								/>
							) : (
								<Typography.Paragraph className="!mb-0">
									{post?.hashtags || hashtags}
								</Typography.Paragraph>
							)}
						</Card>

						<Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
							<Row justify="space-between" align="middle">
								<Typography.Title level={5} style={{ margin: 0 }}>
									{t('postPage.cards.mediaSuggestionsTitle')}
								</Typography.Title>
								<Space>
									<Button icon={<CopyOutlined />} onClick={() => clipboardCopy(post?.media || '')}>
										{t('postPage.buttons.copy')}
									</Button>
									{renderEditButton('offer', editOffer, changeOffer, isReadOnly)}
								</Space>
							</Row>
							<Divider style={{ margin: '12px 0' }} />
							{editOffer ? (
								<Input.TextArea
									value={offer}
									onChange={(e) => setOffer(e.target.value)}
									autoSize={{ minRows: 3, maxRows: 8 }}
									placeholder="Suggest visuals, themes, or B-roll…"
								/>
							) : (
								<Typography.Paragraph className="!mb-0">
									{post?.media || offer}
								</Typography.Paragraph>
							)}
							{!post?.images?.length && !images.length && !video && !post?.video && (
								<>
									<Divider style={{ margin: '16px 0' }} />
									<div className="text-center">
										{referenceImages.length > 0 && (
											<div className="mb-4">
												<Row justify="space-between" align="middle" className="mb-2">
													<Typography.Text strong>
														{t('postPage.buttons.referenceImages', 'Изображения-референсы')} (
														{referenceImages.length}/{MAX_REFERENCE_IMAGES})
													</Typography.Text>
													<Button type="text" danger size="small" onClick={handleClearReferences}>
														{t('postPage.buttons.clearAll', 'Очистить все')}
													</Button>
												</Row>
												<div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
													{referenceImages.map((refImg, idx) => (
														<div key={idx} className="relative group">
															<img
																src={URL.createObjectURL(refImg)}
																alt={`Reference ${idx + 1}`}
																className="w-full h-24 object-cover rounded-lg border border-gray-300 transition-opacity group-hover:opacity-75"
															/>
															<Button
																type="primary"
																danger
																shape="circle"
																size="small"
																icon={<CloseOutlined />}
																className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
																onClick={() => handleRemoveReference(idx)}
															/>
															<div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded">
																{idx + 1}
															</div>
														</div>
													))}
												</div>
											</div>
										)}
										<Space
											direction={isMobileDevice() ? 'vertical' : 'horizontal'}
											style={{
												width: isMobileDevice() ? '100%' : 'auto',
												gap: isMobileDevice() ? '8px' : 'small',
											}}
										>
											<Button
												icon={<UploadOutlined />}
												onClick={() => hiddenReferenceInput.current?.click()}
												disabled={
													activeEdit || generating || referenceImages.length >= MAX_REFERENCE_IMAGES
												}
												style={isMobileDevice() ? { width: '100%' } : {}}
												size={isMobileDevice() ? 'large' : 'middle'}
											>
												{referenceImages.length > 0
													? t('postPage.buttons.addMoreReferences', 'Добавить ещё')
													: t('postPage.buttons.addReference', 'Добавить референс')}
											</Button>
											<Button
												type="primary"
												icon={<ThunderboltOutlined />}
												onClick={onGenerateImage}
												loading={generating}
												disabled={!!activeEdit}
												style={isMobileDevice() ? { width: '100%' } : {}}
												size={isMobileDevice() ? 'large' : 'middle'}
											>
												{t('postPage.buttons.generate')}
											</Button>
										</Space>
										{referenceImages.length === 0 && (
											<Typography.Text type="secondary" className="block mt-2 text-xs">
												{t('postPage.hint.addReferencesFirst')}
											</Typography.Text>
										)}
									</div>
								</>
							)}
							{!post?.published && (
								<Popover
									overlayStyle={{ maxWidth: 520 }}
									placement="bottom"
									content={
										<pre style={{ fontSize: 12 }} className="block whitespace-pre-wrap">
											{t('postPage.cards.mediaRequirementsBody')}
										</pre>
									}
									title={t('postPage.cards.mediaRequirementsTitle')}
								>
									<div className="flex justify-center gap-1 mt-4 items-center cursor-help">
										<InfoCircleFilled style={{ color: 'blueviolet' }} />
										<span>{t('postPage.misc.imageLabel')}</span>
									</div>
								</Popover>
							)}
						</Card>

						{!post?.published && (
							<Card className="shadow-sm" style={{ marginTop: 16 }} bodyStyle={{ padding: 16 }}>
								<Row justify="space-between" align="middle">
									<Typography.Title level={5} style={{ margin: 0 }}>
										{t('postPage.cards.mediaTitle')}
									</Typography.Title>
									<Space direction="vertical" style={{ width: '100%', gap: '8px' }}>
										{serverImagesCount >= 2 && (
											<Button
												block
												disabled={!!activeEdit}
												onClick={() => setShowReorderModal(true)}
												size={isMobileDevice() ? 'large' : 'middle'}
											>
												{t('postPage.buttons.reorder')}
											</Button>
										)}
										{totalImagesCount < MAX_IMAGES &&
											!(
												post?.collaborators?.collaborators?.length > 0 &&
												totalImagesCount + (video ? 1 : 0) + (post?.video ? 1 : 0) >= 1
											) && (
												<Space
													direction={isMobileDevice() ? 'vertical' : 'horizontal'}
													style={{
														width: isMobileDevice() ? '100%' : 'auto',
														gap: isMobileDevice() ? '8px' : 'small',
													}}
												>
													<Button
														icon={<PictureOutlined />}
														onClick={() => hiddenFileInput.current?.click()}
														disabled={reorderMode || !!activeEdit}
														style={isMobileDevice() ? { width: '100%' } : {}}
														size={isMobileDevice() ? 'large' : 'middle'}
													>
														{t('postPage.buttons.uploadImage')}
													</Button>

													{isMobileDevice() ? (
														<>
															<input
																type="file"
																accept="image/*"
																capture="environment"
																style={{ display: 'none' }}
																id="native-camera-input"
																onChange={async (e) => {
																	const file = e.target.files?.[0];
																	if (!file) return;

																	try {
																		const img = new Image();
																		img.src = URL.createObjectURL(file);

																		img.onload = () => {
																			if (img.width * img.height > 5000000) {
																				const canvas = document.createElement('canvas');
																				const ratio = Math.sqrt(5000000 / (img.width * img.height));
																				canvas.width = img.width * ratio;
																				canvas.height = img.height * ratio;

																				const ctx = canvas.getContext('2d');
																				ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

																				canvas.toBlob(
																					(blob) => {
																						const optimizedFile = new File([blob], file.name, {
																							type: 'image/jpeg',
																						});
																						handleImageFilePicker({
																							target: { files: [optimizedFile] },
																						});
																					},
																					'image/jpeg',
																					0.85,
																				);
																			} else {
																				handleImageFilePicker({ target: { files: [file] } });
																			}

																			URL.revokeObjectURL(img.src);
																		};
																	} catch (error) {
																		console.error('Error processing image:', error);
																		handleImageFilePicker({ target: { files: [file] } });
																	}
																	e.target.value = '';
																}}
															/>
															<Button
																icon={<CameraOutlined />}
																onClick={() =>
																	document.getElementById('native-camera-input')?.click()
																}
																disabled={reorderMode || !!activeEdit}
																style={{ width: '100%' }}
																size={isMobileDevice() ? 'large' : 'middle'}
															>
																{t('postPage.buttons.takePhoto') || 'Take Photo'}
															</Button>
														</>
													) : (
														navigator.mediaDevices?.getUserMedia && (
															<Button
																icon={<CameraOutlined />}
																onClick={() => setShowCameraModal(true)}
																disabled={reorderMode || !!activeEdit}
															>
																{t('postPage.buttons.takePhoto') || 'Take Photo'}
															</Button>
														)
													)}
												</Space>
											)}
										{video || post?.video ? (
											<Button
												danger
												disabled={!!activeEdit}
												icon={<VideoCameraOutlined />}
												onClick={handleRemoveVideo}
												style={isMobileDevice() ? { width: '100%' } : {}}
												size={isMobileDevice() ? 'large' : 'middle'}
											>
												{t('postPage.buttons.removeVideo')}
											</Button>
										) : (
											!post?.images?.length &&
											(contentPlan?.socialNetwork?.includes('Instagram') ||
												contentPlan?.socialNetwork?.includes('Threads')) &&
											!(post?.collaborators?.collaborators?.length > 0 && totalImagesCount > 0) && (
												<Button
													icon={<VideoCameraOutlined />}
													onClick={() => hiddenVideoInput.current?.click()}
													disabled={!!activeEdit}
													style={isMobileDevice() ? { width: '100%' } : {}}
													size={isMobileDevice() ? 'large' : 'middle'}
												>
													{t('postPage.buttons.addVideoReels')}
												</Button>
											)
										)}
									</Space>
								</Row>

								<Divider style={{ margin: '12px 0' }} />

								<div
									onDragEnter={handleDragEnter}
									onDragLeave={handleDragLeave}
									onDragOver={handleDragOver}
									onDrop={handleDrop}
									onClick={
										!!activeEdit
											? undefined
											: post?.images?.length || images?.length
											? null
											: () => hiddenFileInput.current.click()
									}
									className={`${
										post?.images?.length || images.length
											? !!activeEdit
												? 'cursor-not-allowed'
												: 'cursor-pointer'
											: !!activeEdit
											? 'cursor-not-allowed'
											: 'cursor-pointer border-2 border-dashed'
									}
                                         ${
																						!!activeEdit
																							? 'opacity-60'
																							: dragActive
																							? 'border-violet-600 bg-violet-50'
																							: 'border-gray-300'
																					} rounded-xl p-4 min-h-24`}
									aria-disabled={!!activeEdit}
									title={!!activeEdit ? t('postPage.message.finEditFirst') : undefined}
								>
									{images.length ? (
										<div className="grid grid-cols-2 gap-3">
											{images.map((image, index) => (
												<PostPhotoCuter
													key={index}
													index={index}
													image={image}
													handleDeleteImage={handleDeleteImage}
												/>
											))}
										</div>
									) : post?.images?.length ? (
										<div className="grid grid-cols-2 gap-3">
											{(reorderMode
												? pendingOrder.length
													? pendingOrder.map((i) => post.images[i])
													: post.images
												: post.images
											).map((image, index) => {
												const realIndex = reorderMode ? pendingOrder[index] ?? index : index;
												return (
													<div
														key={realIndex}
														draggable={reorderMode}
														onDragStart={onImageDragStart(index)}
														onDragEnter={onImageDragEnter(index)}
														onDragOver={onImageDragOver}
														onDrop={onImageDrop(index)}
														onDragEnd={onImageDragEnd}
														className={`relative rounded-lg ${
															reorderMode && dragOver === index
																? 'outline outline-2 outline-violet-400'
																: ''
														}`}
														title={reorderMode ? t('postPage.hint.reorderHint') : undefined}
														style={{ cursor: reorderMode ? 'grab' : 'default' }}
													>
														{!reorderMode && (
															<Button
																type="primary"
																danger
																shape="circle"
																icon={<DeleteOutlined />}
																size="middle"
																style={{
																	position: 'absolute',
																	top: 6,
																	right: 6,
																	zIndex: 2,
																	backgroundColor: 'rgba(255, 0, 0, 0.85)',
																	border: 'none',
																}}
																disabled={!!activeEdit}
																onClick={() => handleRemoveImage(realIndex)}
															/>
														)}
														<div
															style={{
																pointerEvents: !!activeEdit ? 'none' : 'auto',
																opacity: !!activeEdit ? 0.6 : 1,
															}}
														>
															<PostPhotoEditor
																index={index + 1}
																image={image}
																aspectRange={range}
																onImageChange={async (updatedImageUrl) => {
																	if (reorderMode || !!activeEdit) return;
																	try {
																		if (typeof updatedImageUrl !== 'string') {
																			console.warn('onImageChange: expected a string URL');
																			return;
																		}

																		let file = null;
																		let url = updatedImageUrl;
																		let base64 = null;

																		if (url.startsWith('blob:')) {
																			const resp = await fetch(url);
																			const blob = await resp.blob();
																			file = new File([blob], `edited_${realIndex}.jpg`, {
																				type: blob.type || 'image/jpeg',
																			});
																			url = null;
																		}

																		if (url && url.startsWith('data:')) {
																			base64 = url;
																			url = null;
																		}

																		const isHttpUrl = url && /^https?:\/\//i.test(url);
																		if (!file && !base64 && !isHttpUrl) {
																			console.warn('onImageChange: nothing to send');
																			return;
																		}

																		await dispatch(
																			postActions.replaceImage({
																				postId: post.id,
																				contentPlanId: contentPlan.id,
																				index: realIndex,
																				file,
																				imageUrl: isHttpUrl ? url : null,
																				imageBase64: base64,
																			}),
																		).unwrap();
																	} catch (e) {
																		console.error('Error replacing image:', e);
																	}
																	window.location.reload();
																}}
															/>
														</div>
													</div>
												);
											})}
										</div>
									) : (
										<div className="text-center mt-2 pointer-events-none">
											{!video && !post?.video && (
												<p className="text-base">{t('postPage.dropzone.noImagesYet')}</p>
											)}
											<p className="text-sm text-gray-500">
												{dragActive
													? t('postPage.dropzone.dropFilesToUpload')
													: t('postPage.dropzone.dragDropOrClick')}
											</p>
											<small className="text-xs text-gray-400 block mt-1">
												{t('postPage.dropzone.hint')}
											</small>
										</div>
									)}

									{(video || post?.video) && (
										<div className="mt-4">
											<video
												className="mx-auto rounded-lg"
												onLoadedMetadata={handleManageMetaData}
												ref={videoPlayerRef}
												width="320"
												height="240"
												controls
											>
												<source
													onLoadedMetadata={handleManageMetaData}
													src={post?.video ? post.video : URL.createObjectURL(video)}
													type={video?.type || undefined}
												/>
												Your browser does not support the video tag.
											</video>
										</div>
									)}
									{[...images, video].reduce((s, m) => s + (m?.size || 0), 0) >
										COMBINED_WARN_LIMIT && (
										<p className="text-center text-sm text-red-500 mt-2">
											Large total media size — consider compressing.
										</p>
									)}
								</div>
								<div className="mt-3 grid grid-cols-2 gap-8 justify-items-center">
									{post?.images?.length ? (
										<Button
											danger
											onClick={() => handleRemoveGeneratedImage()}
											disabled={reorderMode || !!activeEdit}
										>
											{t('postPage.buttons.removeImages')}
										</Button>
									) : (
										!!images.length && (
											<Button danger onClick={handleResetImages}>
												{t('postPage.buttons.removeImages')}
											</Button>
										)
									)}
								</div>
							</Card>
						)}
					</Col>
					<Col xs={24} lg={10}>
						<LivePreviewCard
							captionSource={contentPlan?.description || ''}
							attachmentSource={editMainText ? mainText : post?.post || ''}
							serverImages={post?.images}
							profileName={user?.name || 'Company Page'}
							videoUrl={previewVideoUrl}
							threadsHandle={user?.threadsHandle}
							isIgStories={post?.isStories}
						/>
						<div className="mt-4 flex flex-col gap-2">
							{(post?.published_statuses || [])
								.filter((net) => Boolean(net?.url))
								.map((network, idx) => (
									<Button
										key={idx}
										type="default"
										block
										icon={
											network?.name?.toLowerCase() === 'linkedin' ? (
												<LinkedinOutlined />
											) : network?.name?.toLowerCase() === 'facebook' ? (
												<FacebookOutlined />
											) : network?.name?.toLowerCase() === 'instagram' ? (
												<InstagramOutlined />
											) : network?.name?.toLowerCase() === 'threads' ? (
												<img src={threadsIcon} alt="Threads" style={{ width: 14, height: 14 }} />
											) : null
										}
										onClick={() => window.open(network.url, '_blank')?.focus()}
									>
										{t('posts.seePost')} ({network.name})
									</Button>
								))}
							<Popconfirm
								placement="top"
								title={t('post.selectSocialsMedia')}
								description={
									<div className="py-2 flex items-center justify-center gap-4">
										<img
											title="LinkedIn"
											src={linkedInIcon}
											className={`cursor-pointer w-8 h-8 object-contain ${
												snForAnotherPost.includes('LinkedIn') ? '' : 'opacity-40'
											}`}
											onClick={() => handleToogleSnForAnotherPost('LinkedIn')}
										/>
										<img
											title="Facebook"
											src={facebookIcon}
											className={`cursor-pointer w-9 h-9 object-contain ${
												snForAnotherPost.includes('Facebook') ? '' : 'opacity-40'
											}`}
											onClick={() => handleToogleSnForAnotherPost('Facebook')}
										/>
										<img
											title="Instagram"
											src={instagramIcon}
											className={`cursor-pointer w-9 h-9 object-contain ${
												snForAnotherPost.includes('Instagram') ? '' : 'opacity-40'
											}`}
											onClick={() => handleToogleSnForAnotherPost('Instagram')}
										/>
										<img
											title="Threads"
											src={threadsContentIcon}
											className={`cursor-pointer w-8 h-8 object-contain ${
												snForAnotherPost.includes('Threads') ? '' : 'opacity-40'
											}`}
											onClick={() => handleToogleSnForAnotherPost('Threads')}
										/>
									</div>
								}
								onConfirm={handleGenerateAnotherPost}
								okButtonProps={{ disabled: !snForAnotherPost.length }}
								okText={t('functions.generate')}
								cancelText={t('functions.close')}
								icon={<InfoCircleFilled style={{ color: 'blueviolet' }} />}
							>
								{isMobile ? (
									<Button
										block
										className="mt-1"
										icon={<SendOutlined />}
										type="primary"
										onClick={onOkayButton}
										disabled={activeEdit !== null}
									>
										{t('post.saveBut')}
									</Button>
								) : null}
								<Button block type="primary" className="mt-1 mb-1" disabled={activeEdit !== null}>
									{t('posts.createAnotherPost')}
								</Button>
								<PostRegenerateEditor
									disabled={postLoading || postGenerating || reorderMode || activeEdit !== null}
									onConfirm={(customPrompt) => {
										const body = {
											originalPostBody: post?.post ?? '',
											originalPostHashtags: post?.hashtags ?? '',
											originalPostImageRecommendations: post?.media ?? '',
											userInstructions: (customPrompt || '').trim(),
										};
										dispatch(
											postActions.regeneratePost({
												postId: post.id,
												contentPlanId: contentPlan.id,
												postData: body,
											}),
										)
											.then(() => message.success('Successfully generated'))
											.catch(() => message.error('Something went wrong'));
									}}
								/>
							</Popconfirm>
						</div>
					</Col>
				</Row>
				<input
					type="file"
					accept="image/*"
					onChange={handleReferenceImageSelect}
					ref={hiddenReferenceInput}
					multiple
					style={{ display: 'none' }}
				/>
				<input
					type="file"
					accept="image/*"
					onChange={handleImageFilePicker}
					ref={hiddenFileInput}
					multiple
					style={{ display: 'none' }}
				/>
				<input
					onChange={handleAddVideo}
					ref={hiddenVideoInput}
					type="file"
					accept="video/mp4, video/mov, video/wmv, video/avi, video/flv, video/mpeg, video/mpg, video/quicktime, video/3gpp, video/webm"
					className="hidden"
				/>
				<KonvaImageEditorModal
					visible={forceEdit.visible}
					imageUrl={forceEdit.objectUrl}
					onSave={handleForceEditorSave}
					onCancel={() => dispatchForceEdit({ type: 'CANCEL' })}
				/>
				<CameraModal
					visible={showCameraModal}
					onCapture={(file) => {
						handleCameraCapture(file);
						setShowCameraModal(false);
					}}
					onCancel={() => setShowCameraModal(false)}
				/>
				<ReorderImagesModal
					open={showReorderModal}
					images={post?.images || []}
					onClose={() => setShowReorderModal(false)}
					onSave={handleSaveModalOrder}
				/>
				<ImageEditorModal
					visible={showImageEditor}
					imageUrl={
						editingImage?.image instanceof File
							? URL.createObjectURL(editingImage.image)
							: editingImage?.image
					}
					onSave={handleSaveEditedImage}
					onCancel={() => {
						setShowImageEditor(false);
						setEditingImage(null);
					}}
					aspectRatio={
						contentPlan?.socialNetwork?.includes('Instagram') && post?.isStories
							? '9:16'
							: contentPlan?.socialNetwork?.includes('Instagram')
							? '1:1'
							: null
					}
				/>
			</Layout.Content>
		</Layout>
	);
};
